import { useState } from 'react';
import {
    Avatar, Card, Flex, Space, Tag, Typography,
} from 'antd';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { BookingStopStatus } from '../../interfaces/booking.interface';
import { DisplayStop } from '../../utils/multiPicking';

const { Text } = Typography;

const STATUS_TAG_COLOR: Record<BookingStopStatus, string> = {
    PENDING: 'default',
    ACCEPTED: 'blue',
    PICKED_UP: 'green',
    COMPLETED: 'green',
    FAILED: 'red',
};

const STATUS_LABEL: Record<BookingStopStatus, string> = {
    PENDING: 'Pendiente',
    ACCEPTED: 'Aceptado',
    PICKED_UP: 'Recogido',
    COMPLETED: 'Completado',
    FAILED: 'Fallido',
};

interface RouteStopsProps {
    stops: DisplayStop[];
    hoveredId: string | null;
    onHover: (id: string | null) => void;
}

const Detail = ({ label, value }: { label: string; value: string }) => (
    <Flex gap={8}>
        <Text type="secondary" style={{ minWidth: 90, fontSize: 12 }}>{label}</Text>
        <Text style={{ fontSize: 12 }}>{value}</Text>
    </Flex>
);

const RouteStops = ({ stops, hoveredId, onHover }: RouteStopsProps) => {
    const [open, setOpen] = useState<Record<string, boolean>>(
        () => stops.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}),
    );

    const toggle = (id: string) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {stops.map((stop) => {
                const isDelivery = stop.kind === 'DELIVERY';
                const expanded = open[stop.id];
                const isHovered = hoveredId === stop.id;
                return (
                    <Card
                        key={stop.id}
                        size="small"
                        styles={{ body: { padding: 0 } }}
                        onMouseEnter={() => onHover(stop.id)}
                        onMouseLeave={() => onHover(null)}
                        style={{
                            borderColor: isHovered ? '#00B2E3' : undefined,
                            boxShadow: isHovered ? '0 0 0 2px rgba(0,178,227,.2)' : undefined,
                            transition: 'all .12s',
                        }}
                    >
                        <Flex
                            align="center"
                            gap={10}
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(stop.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') toggle(stop.id); }}
                            style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                background: isHovered ? 'rgba(0,178,227,.06)' : undefined,
                            }}
                        >
                            <Avatar
                                size={26}
                                style={{
                                    flex: '0 0 auto',
                                    backgroundColor: isDelivery ? '#eb2f96' : '#00B2E3',
                                    fontWeight: 700,
                                    fontSize: 12,
                                }}
                            >
                                {isDelivery ? '🏁' : stop.order}
                            </Avatar>
                            <Space size={6} wrap style={{ flex: 1 }}>
                                <Text strong>{stop.business || stop.address || 'N/A'}</Text>
                                <Tag color={isDelivery ? 'purple' : 'geekblue'}>{stop.kind}</Tag>
                                <Tag color={STATUS_TAG_COLOR[stop.status] || 'default'}>
                                    {STATUS_LABEL[stop.status] || stop.status}
                                </Tag>
                            </Space>
                            <Text type="secondary" style={{ display: 'flex' }}>
                                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                            </Text>
                        </Flex>
                        {expanded && (
                            <Space
                                direction="vertical"
                                size={2}
                                style={{ width: '100%', padding: '0 12px 12px 48px' }}
                            >
                                <Detail label="Dirección" value={stop.address || 'N/A'} />
                                <Detail label="Referencia" value={stop.references || 'N/A'} />
                                <Detail label="Coordenadas" value={`${stop.lat.toFixed(5)}, ${stop.lng.toFixed(5)}`} />
                            </Space>
                        )}
                    </Card>
                );
            })}
        </Space>
    );
};

export { RouteStops };
